import {
	Skeleton,
	Tooltip,
	Box,
	Button,
	useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePipelineContext } from '@/context/pipelineContext';
import { useSelector } from 'react-redux';
import { addCamelCaseKeys, getCreateUrlFromPipeline, getPipelineIcon } from '@/utils/constants';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { Add, LibraryAddOutlined } from '@mui/icons-material';
import { KanbanToggle } from '@/components/buttons/KanbanToggle';
import { BulkRenewalsDialog } from '@/components/dialogs/BulkRenewalsDialog';
import { BulkDebtorsDialog } from '@/components/dialogs/BulkDebtorsDialog';
import { PageHeader } from '@/components/layouts/PageHeader';
import { PageTabsBar } from '@/components/layouts/PageHeader/PageTabsBar';
import { getDealClientId } from '@/features/deals/helper';

const generateRenewalState = (selected, isPolicySelectionRenewalsTab) => ({
	...(!isPolicySelectionRenewalsTab && {
		dealIds: selected.map((d) => d.id),
		dealFolder: selected.flatMap((folders) => folders.yearFolder),
	}),
	clientId: getDealClientId(selected[0]),

	...(isPolicySelectionRenewalsTab && {
		policies: selected.map((policy) => ({
			...addCamelCaseKeys(policy),
			policyTransId: policy.CurrentPolicy,
			insightId: policy.Id,
			Id: policy.Id,
			renewalDate: policy.ToDate,
		})),
	}),
});

const generateDebtorState = (selected) => ({
	clientId: getDealClientId(selected[0]),
	transactions: selected,
});

const debtorTabs = [
	{ label: 'Awaiting payment', value: 'awaiting-payment' },
	{ label: 'Premium funded', value: 'premium-funded' },
	{ label: 'Completed', value: 'completed' },
	{ label: 'Invoice report', value: 'invoice-report' },
];

export const DealPipelineTabs = ({ pipelineId, currentTab, onTabChange }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();

	const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
	const [debtorDialogOpen, setDebtorDialogOpen] = useState(false);

	const isRenewals = pipelineId === process.env.REACT_APP_PIPELINE_RENEWALS;
	const isClaims = pipelineId == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isNewBusiness = pipelineId === process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isEndorsements = pipelineId === process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isDebtors = pipelineId === process.env.REACT_APP_PIPELINE_DEBTORS;

	const hasInvoicedTab = isNewBusiness || isRenewals || isEndorsements;

	const { getFeatureFlags } = useFeatureFlagContext();
	const showDebtors = Boolean(getFeatureFlags('debtors')?.active);

	const { kanbanView } = useSelector((state) => state.userPrefs);
	const { selected } = useSelector((state) => state.dealTable);

	const { stages, pipeline, ...pipelineQuery } = usePipelineContext(pipelineId);
	const pipelineName = pipeline?.label;
	const isLoading = !pipelineQuery.isSuccess && !pipelineQuery.isError;

	const invoicedTabLabel = useMemo(() => {
		const completedStage = stages?.find((stage) => stage.completed);
		return completedStage?.label ?? 'Invoiced';
	}, [stages]);

	const tabs = useMemo(
		() =>
			isDebtors
				? debtorTabs
				: [
						...(isClaims || isRenewals || isNewBusiness || isEndorsements
							? [{ label: 'In progress', value: 'active' }]
							: []),
						...(isClaims ? [{ label: 'Closed', value: 'closed' }] : []),
						...(hasInvoicedTab ? [{ label: invoicedTabLabel, value: 'invoiced' }] : []),
						...(isRenewals ? [{ label: 'Renewal Report', value: 'policies' }] : []),
					],
		[
			isDebtors,
			isClaims,
			isRenewals,
			isNewBusiness,
			isEndorsements,
			hasInvoicedTab,
			invoicedTabLabel,
		],
	);

	const hasTabs = tabs.length > 0;

	useEffect(() => {
		if (hasTabs && !tabs.some((tab) => tab.value === currentTab)) {
			navigate(location.pathname, { replace: true });
		}
	}, [currentTab, navigate, location.pathname, tabs, hasTabs]);

	const handleTabChange = (event, value) => {
		if (onTabChange) {
			onTabChange(value);
		}
	};

	const isInvoiceReportTab = isDebtors && currentTab === 'invoice-report';

	const shouldHideKanban =
		(isDebtors && !showDebtors) ||
		isInvoiceReportTab ||
		(isRenewals && (currentTab === 'policies' || currentTab === 'archived'));

	const isPolicySelectionRenewalsTab = isRenewals && currentTab === 'policies';

	const createPipelineName = useMemo(() => {
		if (pipelineId == process.env.REACT_APP_PIPELINE_NEW_BUSINESS) {
			return pipelineName;
		} else {
			return `New ${pipelineName?.substring(0, pipelineName.length - 1)}`;
		}
	}, [pipelineId, pipelineName]);

	const navigationStateParams = useMemo(() => {
		const params =
			selected.length > 0
				? {
						state: {
							...(isRenewals &&
								generateRenewalState(selected, isPolicySelectionRenewalsTab)),
							...(isDebtors && generateDebtorState(selected)),
						},
					}
				: null;
		return params;
	}, [selected, isRenewals, isDebtors, isPolicySelectionRenewalsTab]);

	const handleNavigation = useCallback(() => {
		const createUrl = getCreateUrlFromPipeline(pipelineId);
		if (createUrl) {
			navigate(createUrl, navigationStateParams);
		}
	}, [pipelineId, navigationStateParams, navigate]);

	const hasSelection =
		selected.length > 0 &&
		new Set(
			selected.map((d) => d?.clientDocId ?? d?.ClientDocId ?? d?.ClientId),
		).size !== 1;

	const renewalDisabledDueToClient = isRenewals && hasSelection;
	const renewalDisabled = isPolicySelectionRenewalsTab ? renewalDisabledDueToClient : false;

	const debtorDisabledDueToClient = isDebtors && hasSelection;
	const debtorCreationDisabled = debtorDisabledDueToClient && isInvoiceReportTab;

	const tooltipTitle = renewalDisabledDueToClient
		? 'Please select deals from one client in order to bundle renewals'
		: debtorCreationDisabled
			? 'Please select transactions from one client'
			: '';

	const showSelection =
		selected.length > 0 && (isPolicySelectionRenewalsTab || isInvoiceReportTab);

	const createButton = (
		<Tooltip title={tooltipTitle}>
			<Box>
				<Button
					variant='contained'
					disabled={renewalDisabled || debtorCreationDisabled}
					startIcon={<Add />}
					onClick={handleNavigation}
					sx={{ mr: 1 }}
				>
					{createPipelineName}
					{showSelection && (
						<span style={{ marginLeft: '0.25em' }}>{`(${selected.length})`}</span>
					)}
				</Button>
			</Box>
		</Tooltip>
	);

	const headerElement = (
		<>
			{!shouldHideKanban && <KanbanToggle />}
			{isRenewals && isPolicySelectionRenewalsTab && (
				<Box>
					<Button
						variant='contained'
						startIcon={<LibraryAddOutlined />}
						onClick={() => setRenewalDialogOpen(true)}
					>
						Create bulk renewals
					</Button>
				</Box>
			)}
			{isInvoiceReportTab && (
				<Box>
					<Button
						variant='contained'
						startIcon={<LibraryAddOutlined />}
						onClick={() => setDebtorDialogOpen(true)}
					>
						Create bulk debtors
					</Button>
				</Box>
			)}
			{createButton}
		</>
	);

	if (isLoading) {
		return (
			<Skeleton
				variant='rectangular'
				height={48}
				width='100%'
				sx={{ px: 2, borderRadius: 1 }}
			/>
		);
	}

	if (!hasTabs) {
		return null;
	}

	const visibleTabs = kanbanView && !shouldHideKanban ? [] : tabs;

	return (
		<>
			<PageHeader
				title={pipelineName}
				icon={getPipelineIcon(
					pipelineId,
					'40px',
					theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
				)}
			/>
			<PageTabsBar
				tabs={visibleTabs}
				value={currentTab}
				onChange={handleTabChange}
				queryParam='tab'
				actions={headerElement}
			/>
			<BulkRenewalsDialog
				open={renewalDialogOpen}
				onClose={() => setRenewalDialogOpen(false)}
			/>
			<BulkDebtorsDialog open={debtorDialogOpen} onClose={() => setDebtorDialogOpen(false)} />
		</>
	);
};

DealPipelineTabs.displayName = 'DealPipelineTabs';
